import spcBlackbutt from "@/assets/products/spc-blackbutt.jpg";
import spcSpottedGum from "@/assets/products/spc-spotted-gum.jpg";
import spcEuropeanOak from "@/assets/products/spc-european-oak.jpg";
import spcTasmanianOak from "@/assets/products/spc-tasmanian-oak.jpg";
import spcGreyOak from "@/assets/products/spc-grey-oak.jpg";
import spcForestOak from "@/assets/products/spc-forest-oak.jpg";
import spcNaturalOak from "@/assets/products/spc-natural-oak.jpg";
import spcTravertineStone from "@/assets/products/spc-travertine-stone.jpg";
import spcTravertineGrey from "@/assets/products/spc-travertine-grey.jpg";
import spcCarraraMarble from "@/assets/products/spc-carrara-marble.jpg";

// Installed / room scene images
import spcBlackbuttInstalled from "@/assets/products/spc-blackbutt-installed.png";
import spcSpottedGumInstalled from "@/assets/products/spc-spotted-gum-installed.jpg";
import spcEuropeanOakInstalled from "@/assets/products/spc-european-oak-installed.jpg";
import spcTasmanianOakInstalled from "@/assets/products/spc-tasmanian-oak-installed.jpg";
import spcGreyOakInstalled from "@/assets/products/spc-grey-oak-installed.jpg";
import spcForestOakInstalled from "@/assets/products/spc-forest-oak-installed.jpg";
import spcNaturalOakInstalled from "@/assets/products/spc-natural-oak-installed.jpg";
import spcTravertineStoneInstalled from "@/assets/products/spc-travertine-stone-installed.jpg";
import spcTravertineGreyInstalled from "@/assets/products/spc-travertine-grey-installed.jpg";
import spcCarraraMarbleInstalled from "@/assets/products/spc-carrara-marble-installed.jpg";

export type SPCRange = "wood" | "stone";

export interface SPCFlooringProduct {
  id: string;
  name: string;
  colour: string;
  range: SPCRange;
  image: string;
  installedImage?: string;
  description: string;
  pricePerSqm: number;
  pricePerBox: number;
  coveragePerBox: number;
  /** Coverage used only to display the per-m² price, so location coverage corrections never change prices. */
  pricingCoveragePerBox?: number;
  piecesPerBox: number;
  thickness: string;
  coreThickness?: string;
  underlay?: string;
  wearLayer?: string;
  plankSize?: string;
  tileSize?: string;
  slipRating: string;
  waterproof: boolean;
  installation: string;
  certifications?: string;
  warrantyResidential?: string;
  warrantyCommercial?: string;
  applications: string[];
  features: string[];
  /** Real Shopify GraphQL variant ID for checkout */
  shopifyVariantId: string;
}

const woodFeatures = [
  "Durable click lock floating floor system",
  "Suitable for heavy residential and commercial installation",
  "Deep embossed realistic timber texture",
  "High heel & furniture impact resistant",
  "100% Waterproof",
  "Scratch & stain resistant",
  "Pet friendly",
  "Easy DIY locking system",
  "Easy to clean & maintain",
  "100% Phthalate, Silica & VOC free",
  "Can be installed over existing tiles for cost-effective renovation",
];

const stoneFeatures = [
  "Durable click lock floating floor system",
  "Suitable for heavy residential and commercial installation",
  "Realistic stone texture finish",
  "High heel & furniture impact resistant",
  "100% Waterproof",
  "Scratch & stain resistant",
  "Pet friendly",
  "Easy DIY locking system",
  "Easy to clean & maintain",
  "100% Phthalate, Silica & VOC free",
  "Can be installed over existing tiles for cost-effective renovation",
];

const woodApps = ["Living rooms", "Bedrooms", "Hallways", "Rentals", "Offices", "Retail"];
const stoneApps = ["Living rooms", "Kitchens", "Hallways", "Shops", "Offices"];

// Wood: $28/m² × 1.737 m²/box = $48.64 per box → round to $48.64
// Stone: $24/m² × 1.675 m²/box = $40.20 per box → round to $40.20

export const SPC_FLOORING_PRODUCTS: SPCFlooringProduct[] = [
  // ===== WOOD LOOK 7.5mm =====
  {
    id: "spc-blackbutt",
    name: "Blackbutt",
    colour: "Blackbutt",
    range: "wood",
    image: spcBlackbutt,
    installedImage: spcBlackbuttInstalled,
    description: "Warm honey tones with natural grain character. A classic Australian timber look for modern interiors.",
    pricePerSqm: 28,
    pricePerBox: 48.64,
    coveragePerBox: 1.737,
    piecesPerBox: 5,
    thickness: "7.5mm",
    coreThickness: "6.0mm SPC rigid limestone core",
    underlay: "1.5mm IXPE acoustic backing",
    wearLayer: "0.5mm",
    plankSize: "1524mm (L) × 228mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    certifications: "ISO 9001, ISO 14001",
    warrantyResidential: "15 years",
    warrantyCommercial: "10 years",
    applications: woodApps,
    features: woodFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47688786149617",
  },
  {
    id: "spc-spotted-gum",
    name: "Spotted Gum",
    colour: "Spotted Gum",
    range: "wood",
    image: spcSpottedGum,
    installedImage: spcSpottedGumInstalled,
    description: "Rich earthy tones with distinctive figured grain. Captures the essence of one of Australia's most loved timbers.",
    pricePerSqm: 28,
    pricePerBox: 48.64,
    coveragePerBox: 1.737,
    piecesPerBox: 5,
    thickness: "7.5mm",
    coreThickness: "6.0mm SPC rigid limestone core",
    underlay: "1.5mm IXPE acoustic backing",
    wearLayer: "0.5mm",
    plankSize: "1524mm (L) × 228mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    certifications: "ISO 9001, ISO 14001",
    warrantyResidential: "15 years",
    warrantyCommercial: "10 years",
    applications: woodApps,
    features: woodFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47688790835441",
  },
  {
    id: "spc-european-oak",
    name: "European Oak",
    colour: "European Oak",
    range: "wood",
    image: spcEuropeanOak,
    installedImage: spcEuropeanOakInstalled,
    description: "Warm golden-brown oak with refined, uniform grain. A versatile classic for contemporary and traditional spaces.",
    pricePerSqm: 28,
    pricePerBox: 48.64,
    coveragePerBox: 1.737,
    piecesPerBox: 5,
    thickness: "7.5mm",
    coreThickness: "6.0mm SPC rigid limestone core",
    underlay: "1.5mm IXPE acoustic backing",
    wearLayer: "0.5mm",
    plankSize: "1524mm (L) × 228mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    certifications: "ISO 9001, ISO 14001",
    warrantyResidential: "15 years",
    warrantyCommercial: "10 years",
    applications: woodApps,
    features: woodFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47688786215153",
  },
  {
    id: "spc-tasmanian-oak",
    name: "Tasmanian Oak",
    colour: "Tasmanian Oak",
    range: "wood",
    image: spcTasmanianOak,
    installedImage: spcTasmanianOakInstalled,
    description: "Soft sandy-beige tones with subtle natural grain. A light, airy timber look ideal for open-plan living.",
    pricePerSqm: 28,
    pricePerBox: 48.64,
    coveragePerBox: 1.737,
    piecesPerBox: 5,
    thickness: "7.5mm",
    coreThickness: "6.0mm SPC rigid limestone core",
    underlay: "1.5mm IXPE acoustic backing",
    wearLayer: "0.5mm",
    plankSize: "1524mm (L) × 228mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    certifications: "ISO 9001, ISO 14001",
    warrantyResidential: "15 years",
    warrantyCommercial: "10 years",
    applications: woodApps,
    features: woodFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47688794145009",
  },
  {
    id: "spc-grey-oak",
    name: "Grey Oak",
    colour: "Grey Oak",
    range: "wood",
    image: spcGreyOak,
    installedImage: spcGreyOakInstalled,
    description: "Cool grey tones with dramatic grain detail. A bold contemporary choice for modern commercial and residential fit-outs.",
    pricePerSqm: 28,
    pricePerBox: 48.64,
    coveragePerBox: 1.737,
    piecesPerBox: 5,
    thickness: "7.5mm",
    coreThickness: "6.0mm SPC rigid limestone core",
    underlay: "1.5mm IXPE acoustic backing",
    wearLayer: "0.5mm",
    plankSize: "1524mm (L) × 228mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    certifications: "ISO 9001, ISO 14001",
    warrantyResidential: "15 years",
    warrantyCommercial: "10 years",
    applications: woodApps,
    features: woodFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47688787591409",
  },
  {
    id: "spc-forest-oak",
    name: "Forest Oak",
    colour: "Forest Oak",
    range: "wood",
    image: spcForestOak,
    installedImage: spcForestOakInstalled,
    description: "Smoky olive-brown with weathered grain character. A sophisticated tone for feature rooms and open-plan spaces.",
    pricePerSqm: 28,
    pricePerBox: 48.64,
    coveragePerBox: 1.737,
    piecesPerBox: 5,
    thickness: "7.5mm",
    coreThickness: "6.0mm SPC rigid limestone core",
    underlay: "1.5mm IXPE acoustic backing",
    wearLayer: "0.5mm",
    plankSize: "1524mm (L) × 228mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    certifications: "ISO 9001, ISO 14001",
    warrantyResidential: "15 years",
    warrantyCommercial: "10 years",
    applications: woodApps,
    features: woodFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47688786411761",
  },
  {
    id: "spc-natural-oak",
    name: "Natural Oak",
    colour: "Natural Oak",
    range: "wood",
    image: spcNaturalOak,
    installedImage: spcNaturalOakInstalled,
    description: "Silver-grey oak with rustic knot features. A distinctive, weathered aesthetic for statement interiors.",
    pricePerSqm: 28,
    pricePerBox: 48.64,
    coveragePerBox: 1.737,
    piecesPerBox: 5,
    thickness: "7.5mm",
    coreThickness: "6.0mm SPC rigid limestone core",
    underlay: "1.5mm IXPE acoustic backing",
    wearLayer: "0.5mm",
    plankSize: "1524mm (L) × 228mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    certifications: "ISO 9001, ISO 14001",
    warrantyResidential: "15 years",
    warrantyCommercial: "10 years",
    applications: woodApps,
    features: woodFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47688790769905",
  },
  // ===== STONE LOOK 6.5mm =====
  {
    id: "spc-travertine-stone",
    name: "Travertine Stone",
    colour: "Travertine Stone",
    range: "stone",
    image: spcTravertineStone,
    installedImage: spcTravertineStoneInstalled,
    description: "Light grey stone with subtle natural veining. A clean, modern tile look without the cold underfoot feel.",
    pricePerSqm: 24,
    pricePerBox: 40.20,
    coveragePerBox: 1.675,
    piecesPerBox: 9,
    thickness: "6.5mm",
    tileSize: "610mm (L) × 305mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    applications: stoneApps,
    features: stoneFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47821101826289",
  },
  {
    id: "spc-travertine-grey",
    name: "Travertine Grey",
    colour: "Travertine Grey",
    range: "stone",
    image: spcTravertineGrey,
    installedImage: spcTravertineGreyInstalled,
    description: "Mid-grey travertine with warm gold veining accents. A timeless stone look for kitchens and living areas.",
    pricePerSqm: 24,
    pricePerBox: 40.20,
    coveragePerBox: 1.675,
    piecesPerBox: 9,
    thickness: "6.5mm",
    tileSize: "610mm (L) × 305mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    applications: stoneApps,
    features: stoneFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47821101859057",
  },
  {
    id: "spc-carrara-marble",
    name: "Carrara Marble",
    colour: "Carrara Marble",
    range: "stone",
    image: spcCarraraMarble,
    installedImage: spcCarraraMarbleInstalled,
    description: "Bright white marble with dramatic grey veining. A luxurious, high-end look for any room in the home.",
    pricePerSqm: 24,
    pricePerBox: 40.20,
    coveragePerBox: 1.675,
    piecesPerBox: 9,
    thickness: "6.5mm",
    tileSize: "610mm (L) × 305mm (W)",
    slipRating: "P4",
    waterproof: true,
    installation: "Click lock floating floor",
    applications: stoneApps,
    features: stoneFeatures,
    shopifyVariantId: "gid://shopify/ProductVariant/47820867207409",
  },
];

export const WOOD_PRODUCTS = SPC_FLOORING_PRODUCTS.filter(p => p.range === "wood");
export const STONE_PRODUCTS = SPC_FLOORING_PRODUCTS.filter(p => p.range === "stone");

/**
 * Adelaide (SA) stocks the wood-look range in 7mm only (5.5mm core + 1.5mm
 * IXPE backing) with 2.085 m² per box. Prices are unchanged: the per-m² price
 * keeps using the original coverage via pricingCoveragePerBox.
 */
export const SA_WOOD_COVERAGE_PER_BOX = 2.085;

export function localizeSPCProduct(product: SPCFlooringProduct, state: string | null | undefined): SPCFlooringProduct {
  if (state !== "SA" || product.range !== "wood") return product;
  return {
    ...product,
    thickness: "7mm",
    coreThickness: "5.5mm SPC rigid limestone core",
    coveragePerBox: SA_WOOD_COVERAGE_PER_BOX,
    pricingCoveragePerBox: product.pricingCoveragePerBox ?? product.coveragePerBox,
  };
}

export function spcRangeLabel(product: SPCFlooringProduct): string {
  return product.range === "wood" ? `Wood Look ${product.thickness}` : `Stone Look ${product.thickness}`;
}
